
const CLOUDINARY_CONFIG = {
  cloudName: "dddikocd6",
  uploadPreset: "upload_preset" 
};

const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CONFIG.cloudName}/upload`;


export const cloudinaryService = {
  /**

   * @param {File} file
   * @param {string} userId 
   * @returns {Promise<{success: boolean, secure_url?: string, public_id?: string, error?: string}>}
   */
  async uploadResume(file, userId) {
    try {
      // Validate Cloudinary config
      if (!this.validateConfig()) {
        throw new Error('Cloudinary configuration is missing. Please check your environment variables.');
      }

      // Validate file type
      if (!file.type.includes('pdf')) {
        throw new Error('Only PDF files are allowed');
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size must be less than 5MB');
      }

      // Prepare form data for Cloudinary upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_CONFIG.uploadPreset);
      formData.append('folder', `job-applications/resumes/${userId}`);
      
      // Optional: Add tags and context for better organization
      formData.append('tags', 'resume,job-application');
      formData.append('context', `user_id=${userId}`);

      // Upload to Cloudinary
      const response = await fetch(CLOUDINARY_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      const data = await response.json();

      return {
        success: true,
        secure_url: data.secure_url,
        public_id: data.public_id,
        format: data.format,
        bytes: data.bytes,
        resource_type: data.resource_type
      };

    } catch (error) {
      console.error('Error uploading to Cloudinary:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Delete resume file from Cloudinary
   * @param {string} publicId - Cloudinary public_id of the file to delete
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteResume(publicId) {
    try {
      // Note: Delete operations should be handled server-side for security
      // This is a placeholder for server-side implementation
      console.warn('Cloudinary delete operations should be handled server-side. Public ID:', publicId);
      
      return {
        success: true,
        message: 'Delete operation should be handled server-side'
      };

    } catch (error) {
      console.error('Error in delete operation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Generate a secure URL for PDF preview
   * @param {string} publicId - Cloudinary public_id
   * @param {Object} options - Transformation options
   * @returns {string} - Transformed URL
   */
  generatePreviewUrl(publicId, options = {}) {
    const baseUrl = `https://res.cloudinary.com/${CLOUDINARY_CONFIG.cloudName}/image/upload`;
    const transformations = [
      'fl_attachment', // Force download
      'fl_sanitize', // Sanitize PDF
      ...Object.entries(options).map(([key, value]) => `${key}_${value}`)
    ].join(',');

    return `${baseUrl}/${transformations}/${publicId}.pdf`;
  },

  /**
   * Validate Cloudinary configuration
   * @returns {boolean} - Whether configuration is valid
   */
  validateConfig() {
    if (!CLOUDINARY_CONFIG.cloudName || CLOUDINARY_CONFIG.cloudName === 'your_cloud_name_here') {
      console.error('Cloudinary cloud name is missing. Please set REACT_APP_CLOUDINARY_CLOUD_NAME in your .env file.');
      return false;
    }
    if (!CLOUDINARY_CONFIG.uploadPreset || CLOUDINARY_CONFIG.uploadPreset === 'your_upload_preset_name_here') {
      console.error('Cloudinary upload preset is missing. Please set REACT_APP_CLOUDINARY_UPLOAD_PRESET in your .env file.');
      return false;
    }
    return true;
  }
};

export default cloudinaryService;